import { capitalize, typeset } from "@mesketch/i18n";
import { createContext, useContext, type ReactNode } from "react";
import type { Texts } from "../data/api.js";

// Texty metodiky (REQ-015, REQ-031): hodnota v jazyce textů s českou typografií. V náhledu jsou
// texty `ai_draft` a `reviewed` označené a chybějící klíč se ukáže jako označený klíč. Sezení
// uživatele označení nikdy nemá (REQ-031 AK 5).

interface TextsValue {
  readonly texts: Texts;
  readonly locale: string;
  readonly preview: boolean;
}

const TextsContext = createContext<TextsValue>({ texts: {}, locale: "cs", preview: false });

export function TextsProvider({ texts, locale, preview, children }: TextsValue & { children: ReactNode }) {
  return <TextsContext.Provider value={{ texts, locale, preview }}>{children}</TextsContext.Provider>;
}

export function useTexts(): TextsValue & { text: (key: string) => string | undefined } {
  const ctx = useContext(TextsContext);
  return { ...ctx, text: (key) => {
    const t = ctx.texts[key];
    return t === undefined ? undefined : typeset(t.value, ctx.locale);
  } };
}

/** Text metodiky podle klíče. `cap` dá velké první písmeno (jméno typu na začátku). */
export function T({ k, cap = false }: { k: string | null; cap?: boolean }) {
  const { texts, locale, preview } = useContext(TextsContext);
  if (k === null) return null;
  const t = texts[k];
  if (t === undefined) {
    return preview ? (
      <mark className="mk-missing" data-key={k}>
        {k}
      </mark>
    ) : null;
  }
  const value = typeset(cap ? capitalize(t.value, locale) : t.value, locale);
  if (preview && t.status !== "approved") {
    return (
      <mark className="mk-draft" data-status={t.status} data-key={k}>
        {value}
      </mark>
    );
  }
  return <>{value}</>;
}

/** Parametry teaseru (REQ-010): `{noun1}` apod. nahradí hotovými slovy. */
export function fill(template: string, params: Readonly<Record<string, string>>): string {
  return template.replace(/\{(\w+)\}/g, (all, name: string) => params[name] ?? all);
}
